import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormLabel,
    IconButton,
    InputAdornment,
    InputLabel,
    ListItemText,
    Menu,
    MenuItem,
    Radio,
    RadioGroup,
    Paper,
    Select,
    Skeleton,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { useLocation, useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import SubscriptionForm from './SubscriptionForm';
import AppLayout from '../layout/AppLayout';
import AuthContext from '../../context/AuthContext';
import { billingService } from '../../services/api';
import { INK, MUTED, RULE, FigureStrip, formatCount, formatINR } from '../analytics/analyticsUi';

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------
const STATUS_META = {
    active: { label: 'Active', color: '#1BAA52' },
    pending: { label: 'Awaiting payment', color: '#C77700' },
    cancelled: { label: 'Cancelled', color: '#C2410C' },
    expired: { label: 'Expired', color: MUTED }
};
const statusMeta = (status) => STATUS_META[status] || { label: status || 'Unknown', color: MUTED };

const STATUS_FILTERS = [
    { value: '', label: 'All' },
    { value: 'active', label: 'Active' },
    { value: 'pending', label: 'Awaiting payment' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'expired', label: 'Expired' }
];

const CYCLE_LABELS = { monthly: 'Monthly', quarterly: 'Quarterly', annually: 'Annual' };
const CYCLE_MONTHS = { monthly: 1, quarterly: 3, annually: 12 };
const PAYMENT_METHOD_LABELS = {
    credit_card: 'Credit card',
    debit_card: 'Debit card',
    net_banking: 'Net banking',
    upi: 'UPI',
    wallet: 'Wallet',
    offline: 'Offline',
    manual: 'Manual'
};

// The admin list is loaded in one request so the figures, search and filters
// work across every subscription rather than just the visible page.
const FETCH_LIMIT = 1000;
const DAY_MS = 24 * 60 * 60 * 1000;
const RENEWAL_WINDOW_DAYS = 14;

const capitalize = (text) => (text ? `${text.charAt(0).toUpperCase()}${text.slice(1).replace(/_/g, ' ')}` : '');
// Last day of the current month, the default end of cover when cancelling
const endOfThisMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0);
};

// Tally reads dates as DD-MM-YYYY, in Indian time
const tallyDate = (value) => {
    if (!value) return '';
    const parts = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Asia/Kolkata' })
        .formatToParts(new Date(value));
    const get = (type) => parts.find((p) => p.type === type).value;
    return `${get('day')}-${get('month')}-${get('year')}`;
};
const csvCell = (value) => {
    const text = value === null || value === undefined ? '' : String(value);
    return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const formatDate = (value) =>
    value ? new Date(value).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '–';
const formatDateTime = (value) =>
    value
        ? new Date(value).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' })
        : '–';
const formatMoney = (amount, currency = 'INR') => {
    if (!currency || currency === 'INR') return formatINR(amount);
    try {
        return new Intl.NumberFormat('en-IN', { style: 'currency', currency, maximumFractionDigits: 2 }).format(amount || 0);
    } catch (e) {
        return `${currency} ${Number(amount || 0).toFixed(2)}`;
    }
};

const daysUntil = (value) => (value ? Math.ceil((new Date(value).getTime() - Date.now()) / DAY_MS) : null);

const periodHint = (subscription) => {
    const days = daysUntil(subscription.endDate);
    if (days === null) return null;
    if (subscription.status === 'active') {
        if (days < 0) return { text: 'Due for renewal', color: '#C77700' };
        if (days === 0) return { text: 'Ends today', color: '#C77700' };
        if (days <= RENEWAL_WINDOW_DAYS) {
            return { text: `${subscription.autoRenew === false ? 'Ends' : 'Renews'} in ${days} ${days === 1 ? 'day' : 'days'}`, color: '#C77700' };
        }
        return { text: subscription.autoRenew === false ? 'Auto-renew off' : null, color: MUTED };
    }
    return null;
};

const buttonSx = { textTransform: 'none' };
const headRowSx = { '& th': { color: MUTED, fontWeight: 500, borderColor: RULE, py: 1.25, whiteSpace: 'nowrap' } };

// ---------------------------------------------------------------------------
// Small pieces
// ---------------------------------------------------------------------------
const StatusDot = ({ status }) => {
    const meta = statusMeta(status);
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: meta.color, flexShrink: 0 }} />
            <Typography variant="body2" sx={{ color: INK, fontWeight: 500, whiteSpace: 'nowrap' }}>{meta.label}</Typography>
        </Box>
    );
};

const Customer = ({ user }) => {
    if (!user) return <Typography variant="body2" sx={{ color: MUTED }}>Deleted user</Typography>;
    return (
        <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ color: INK, fontWeight: 600 }} noWrap title={user.company || user.name}>
                {user.company || user.name}
            </Typography>
            <Typography variant="body2" sx={{ color: MUTED }} noWrap title={user.email}>
                {user.company ? user.name : user.email}
            </Typography>
        </Box>
    );
};

const Account = ({ account }) => {
    if (!account) return <Typography variant="body2" sx={{ color: MUTED }}>No account</Typography>;
    return (
        <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ color: INK }} noWrap title={account.businessName}>{account.businessName || '–'}</Typography>
            <Typography variant="body2" sx={{ color: MUTED, fontVariantNumeric: 'tabular-nums' }} noWrap>{account.phoneNumber}</Typography>
        </Box>
    );
};

const RowActions = ({ subscription, onMarkPaid, onView, onEdit, onCancel }) => {
    const [anchor, setAnchor] = useState(null);
    const close = () => setAnchor(null);
    const isPending = subscription.status === 'pending';
    const canCancel = !['cancelled', 'expired'].includes(subscription.status);

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
            <Button
                size="small"
                onClick={() => (isPending ? onMarkPaid(subscription) : onView(subscription))}
                sx={{ ...buttonSx, fontWeight: 600, whiteSpace: 'nowrap' }}
            >
                {isPending ? 'Mark as paid' : 'View details'}
            </Button>
            <IconButton
                size="small"
                onClick={(e) => setAnchor(e.currentTarget)}
                aria-label={`More actions for ${subscription.user?.company || subscription.user?.name || 'subscription'}`}
                aria-haspopup="menu"
            >
                <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu
                anchorEl={anchor}
                open={!!anchor}
                onClose={close}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <MenuItem onClick={() => { close(); onEdit(subscription); }}>
                    <ListItemText primary="Edit subscription" />
                </MenuItem>
                {isPending && (
                    <MenuItem onClick={() => { close(); onView(subscription); }}>
                        <ListItemText primary="View details" />
                    </MenuItem>
                )}
                {canCancel && (
                    <MenuItem onClick={() => { close(); onCancel(subscription); }}>
                        <ListItemText primary="Cancel subscription" primaryTypographyProps={{ color: 'error' }} />
                    </MenuItem>
                )}
            </Menu>
        </Box>
    );
};

const Period = ({ subscription }) => {
    const hint = periodHint(subscription);
    return (
        <Box>
            <Typography variant="body2" sx={{ color: INK, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                {formatDate(subscription.startDate)} – {formatDate(subscription.endDate)}
            </Typography>
            {hint?.text && <Typography variant="caption" sx={{ color: hint.color, display: 'block' }}>{hint.text}</Typography>}
        </Box>
    );
};

const Frame = ({ children }) => (
    <Paper variant="outlined" sx={{ borderColor: RULE, borderRadius: 2, overflow: 'hidden' }}>{children}</Paper>
);

const DetailRow = ({ label, children }) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '160px 1fr' }, gap: { xs: 0, sm: 2 }, py: 1, borderTop: `1px solid ${RULE}` }}>
        <Typography variant="body2" sx={{ color: MUTED }}>{label}</Typography>
        <Typography variant="body2" component="div" sx={{ color: INK, fontVariantNumeric: 'tabular-nums', wordBreak: 'break-word' }}>
            {children}
        </Typography>
    </Box>
);

const SubscriptionSummary = ({ subscription }) => (
    <Box sx={{ mb: 2 }}>
        <DetailRow label="Customer">
            {subscription.user ? `${subscription.user.company || subscription.user.name}${subscription.user.company ? ` (${subscription.user.name})` : ''}` : 'Deleted user'}
        </DetailRow>
        <DetailRow label="Account">
            {subscription.whatsappAccount
                ? `${subscription.whatsappAccount.businessName || ''} ${subscription.whatsappAccount.phoneNumber || ''}`.trim()
                : 'No account'}
        </DetailRow>
        <DetailRow label="Plan">
            {capitalize(subscription.plan)} · {CYCLE_LABELS[subscription.billingCycle] || capitalize(subscription.billingCycle) || 'Monthly'}
        </DetailRow>
        <DetailRow label="Amount">{formatMoney(subscription.amount, subscription.currency)}</DetailRow>
    </Box>
);

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
const SubscriptionManagement = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const fullScreenDialog = useMediaQuery(theme.breakpoints.down('sm'));
    const { user } = useContext(AuthContext);

    const [subscriptions, setSubscriptions] = useState([]);
    const [serverTotal, setServerTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);

    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [cycleFilter, setCycleFilter] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [paying, setPaying] = useState(null);
    const [processData, setProcessData] = useState({ transactionId: '', notes: '' });
    const [processing, setProcessing] = useState(false);
    const [viewing, setViewing] = useState(null);
    const [cancelTarget, setCancelTarget] = useState(null);
    const [cancelWhen, setCancelWhen] = useState('month_end');
    const [cancelling, setCancelling] = useState(false);
    const [openRenewDialog, setOpenRenewDialog] = useState(false);
    // Assign (no subscription) or edit (subscription set); opened from the Users page with a customer preselected
    const [formState, setFormState] = useState(() =>
        location.state?.assignUserId ? { open: true, subscription: null, userId: location.state.assignUserId } : { open: false }
    );

    // The subscriptions on screen, exactly as filtered, as a CSV for Tally
    const exportCsv = () => {
        const header = ['Customer Name', 'Company', 'Contact', 'Email', 'GSTIN', 'WhatsApp Number', 'Business Name',
            'Plan', 'Cycle', 'Amount', 'Monthly Value', 'Status', 'Auto Renew', 'Start Date', 'End Date',
            'Payment Method', 'Transaction ID'];
        const rows = filtered.map((s) => [
            s.user?.tallyLedgerName || s.user?.company || s.user?.name || '',
            s.user?.company || '',
            s.user?.name || '',
            s.user?.email || '',
            s.user?.gstin || '',
            s.whatsappAccount?.phoneNumber || '',
            s.whatsappAccount?.businessName || '',
            capitalize(s.plan),
            CYCLE_LABELS[s.billingCycle] || 'Monthly',
            (Number(s.amount) || 0).toFixed(2),
            ((Number(s.amount) || 0) / (CYCLE_MONTHS[s.billingCycle] || 1)).toFixed(2),
            capitalize(s.status),
            s.autoRenew === false ? 'No' : 'Yes',
            tallyDate(s.startDate),
            tallyDate(s.endDate),
            s.paymentMethod || '',
            s.transactionId || ''
        ].map(csvCell).join(','));

        // A byte order mark so Excel and Tally read the names correctly
        const blob = new Blob([`\ufeff${[header.join(','), ...rows].join('\r\n')}\r\n`], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `subscriptions-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        setSuccess(`${filtered.length} ${filtered.length === 1 ? 'subscription' : 'subscriptions'} exported.`);
    };

    const openCancel = (subscription) => { setCancelWhen('month_end'); setCancelTarget(subscription); };
    const openAssign = () => setFormState({ open: true, subscription: null, userId: null });
    const openEdit = (subscription) => setFormState({ open: true, subscription, userId: null });
    const handleSaved = (saved, wasEditing) => {
        setSubscriptions((prev) => (wasEditing
            ? prev.map((s) => (s._id === saved._id ? saved : s))
            : [saved, ...prev]));
        if (!wasEditing) setServerTotal((t) => t + 1);
        setViewing((v) => (v && v._id === saved._id ? saved : v));
        setFormState({ open: false });
        setSuccess(wasEditing ? 'Subscription updated' : 'Subscription assigned. The customer has been notified.');
    };
    const [renewLoading, setRenewLoading] = useState(false);

    // Cancel a subscription: it stops renewing and the customer keeps access to the end date
    const handleCancel = async () => {
        if (!cancelTarget) return;
        try {
            setCancelling(true);
            const response = await billingService.cancelSubscription(cancelTarget._id, cancelWhen);
            const saved = response.data;
            setSubscriptions((prev) => prev.map((s) => (s._id === cancelTarget._id ? { ...s, ...saved, user: s.user, whatsappAccount: s.whatsappAccount } : s)));
            setViewing((v) => (v && v._id === cancelTarget._id ? { ...v, ...saved, user: v.user, whatsappAccount: v.whatsappAccount } : v));
            setSuccess(`${cancelTarget.user?.company || cancelTarget.user?.name || 'Subscription'} cancelled, cover ends ${formatDate(saved.endDate)}. The customer has been notified.`);
            setCancelTarget(null);
        } catch (err) {
            setError(err.response?.data?.error || 'The subscription could not be cancelled. Try again.');
        } finally {
            setCancelling(false);
        }
    };

    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    useEffect(() => {
        if (user && user.role !== 'admin') navigate('/dashboard');
    }, [user, navigate]);

    const fetchSubscriptions = useCallback(async () => {
        try {
            setLoading(true);
            setLoadError(null);
            const response = await billingService.getAllBillings({ page: 1, limit: FETCH_LIMIT });
            const data = response.data || [];
            setSubscriptions(data);
            setServerTotal(response.total ?? data.length);
        } catch (err) {
            setLoadError(err.response?.data?.error || 'Check your connection and try again.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSubscriptions();
    }, [fetchSubscriptions]);

    // Headline figures, computed from the loaded records
    const figures = useMemo(() => {
        const now = Date.now();
        const windowEnd = now + RENEWAL_WINDOW_DAYS * DAY_MS;
        let active = 0;
        let pending = 0;
        let renewing = 0;
        let monthlyRevenue = 0;
        subscriptions.forEach((s) => {
            if (s.status === 'pending') pending += 1;
            if (s.status !== 'active') return;
            active += 1;
            monthlyRevenue += (Number(s.amount) || 0) / (CYCLE_MONTHS[s.billingCycle] || 1);
            const end = s.endDate ? new Date(s.endDate).getTime() : null;
            if (end !== null && s.autoRenew !== false && end <= windowEnd) renewing += 1;
        });
        return { active, pending, renewing, monthlyRevenue };
    }, [subscriptions]);

    const dueNow = useMemo(
        () => subscriptions.filter((s) => s.status === 'active' && s.autoRenew !== false && s.endDate && new Date(s.endDate) <= new Date()).length,
        [subscriptions]
    );

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        return subscriptions.filter((s) => {
            if (statusFilter && s.status !== statusFilter) return false;
            if (cycleFilter && (s.billingCycle || 'monthly') !== cycleFilter) return false;
            if (!term) return true;
            const haystack = [
                s.user?.name, s.user?.email, s.user?.company,
                s.whatsappAccount?.businessName, s.whatsappAccount?.phoneNumber,
                s.plan, s.transactionId
            ].filter(Boolean).join(' ').toLowerCase();
            return haystack.includes(term);
        });
    }, [subscriptions, search, statusFilter, cycleFilter]);

    const hasFilters = !!(search || statusFilter || cycleFilter);
    const maxPage = Math.max(0, Math.ceil(filtered.length / rowsPerPage) - 1);
    const currentPage = Math.min(page, maxPage);
    const visible = filtered.slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage);

    const clearFilters = () => {
        setSearch('');
        setStatusFilter('');
        setCycleFilter('');
        setPage(0);
    };

    // Mark as paid
    const openMarkPaid = (subscription) => {
        setViewing(null);
        setPaying(subscription);
        setProcessData({ transactionId: '', notes: '' });
    };

    const handleProcessChange = (e) => {
        const { name, value } = e.target;
        setProcessData((prev) => ({ ...prev, [name]: value }));
    };

    const handleProcessPayment = async (e) => {
        e?.preventDefault();
        if (!paying || processing) return;
        try {
            setProcessing(true);
            await billingService.processPayment(paying._id, processData);
            setSuccess(`${paying.user?.company || paying.user?.name || 'Subscription'} marked as paid. A paid invoice was created.`);
            setPaying(null);
            fetchSubscriptions();
        } catch (err) {
            setError(err.response?.data?.error || 'The payment could not be recorded. Reload the list and try again.');
        } finally {
            setProcessing(false);
        }
    };

    // Renewals
    const handleProcessMonthlyRenewals = async () => {
        try {
            setRenewLoading(true);
            const response = await billingService.processMonthlyRenewals();
            const renewed = response.data?.renewed ?? 0;
            const expired = response.data?.expired ?? 0;
            setOpenRenewDialog(false);
            if (!renewed && !expired) setSuccess('No subscriptions were due for renewal');
            else setSuccess(`${formatCount(renewed)} renewed, ${formatCount(expired)} expired`);
            fetchSubscriptions();
        } catch (err) {
            setError(err.response?.data?.error || 'Renewals could not be run. Try again in a moment.');
        } finally {
            setRenewLoading(false);
        }
    };

    // -----------------------------------------------------------------------
    // Rendering
    // -----------------------------------------------------------------------
    const pagination = (
        <TablePagination
            component="div"
            count={filtered.length}
            page={currentPage}
            onPageChange={(e, next) => setPage(next)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage="Per page"
            sx={{ borderTop: `1px solid ${RULE}`, color: MUTED }}
        />
    );

    const renderList = () => {
        if (loading && !subscriptions.length) {
            return (
                <Frame>
                    <Box sx={{ p: { xs: 2, sm: 3 } }} aria-busy="true">
                        {[0, 1, 2, 3, 4].map((i) => (
                            <Box key={i} sx={{ display: 'flex', gap: 3, py: 1.5, borderTop: i ? `1px solid ${RULE}` : 'none' }}>
                                <Box sx={{ flex: 2 }}><Skeleton width="60%" /><Skeleton width="40%" /></Box>
                                <Box sx={{ flex: 2, display: { xs: 'none', md: 'block' } }}><Skeleton width="55%" /><Skeleton width="40%" /></Box>
                                <Box sx={{ flex: 1 }}><Skeleton width="70%" /></Box>
                                <Box sx={{ flex: 1, display: { xs: 'none', md: 'block' } }}><Skeleton width="60%" /></Box>
                            </Box>
                        ))}
                    </Box>
                </Frame>
            );
        }

        if (loadError && !subscriptions.length) {
            return (
                <Frame>
                    <Box sx={{ p: { xs: 3, sm: 5 } }}>
                        <Typography sx={{ fontWeight: 600, color: INK, mb: 1 }}>Subscriptions could not be loaded</Typography>
                        <Typography variant="body2" sx={{ color: MUTED, mb: 2.5, maxWidth: '60ch' }}>{loadError}</Typography>
                        <Button variant="outlined" onClick={fetchSubscriptions} sx={buttonSx}>Try again</Button>
                    </Box>
                </Frame>
            );
        }

        if (!filtered.length) {
            return (
                <Frame>
                    <Box sx={{ p: { xs: 3, sm: 5 } }}>
                        {hasFilters ? (
                            <>
                                <Typography sx={{ fontWeight: 600, color: INK, mb: 1 }}>No subscriptions match these filters</Typography>
                                <Typography variant="body2" sx={{ color: MUTED, mb: 2.5 }}>
                                    Try a different customer, account or phone number, or clear the filters to see every subscription.
                                </Typography>
                                <Button variant="outlined" onClick={clearFilters} sx={buttonSx}>Clear filters</Button>
                            </>
                        ) : (
                            <>
                                <Typography sx={{ fontWeight: 600, color: INK, mb: 1 }}>No subscriptions yet</Typography>
                                <Typography variant="body2" sx={{ color: MUTED, maxWidth: '60ch' }}>
                                    Subscriptions appear here when a customer picks a plan for a WhatsApp account. New ones wait for you to mark them as paid.
                                </Typography>
                            </>
                        )}
                    </Box>
                </Frame>
            );
        }

        if (isMobile) {
            return (
                <Frame>
                    <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, opacity: loading ? 0.6 : 1 }} aria-busy={loading}>
                        {visible.map((s, index) => (
                            <Box component="li" key={s._id} sx={{ px: 2, py: 1.75, borderTop: index ? `1px solid ${RULE}` : 'none' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5 }}>
                                    <Customer user={s.user} />
                                    <StatusDot status={s.status} />
                                </Box>
                                <Box sx={{ mt: 1 }}><Account account={s.whatsappAccount} /></Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1, mt: 1 }}>
                                    <Typography variant="body2" sx={{ color: INK }}>
                                        {capitalize(s.plan)}
                                        <Box component="span" sx={{ color: MUTED }}> · {CYCLE_LABELS[s.billingCycle] || 'Monthly'}</Box>
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: INK, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                                        {formatMoney(s.amount, s.currency)}
                                    </Typography>
                                </Box>
                                <Box sx={{ mt: 0.5 }}><Period subscription={s} /></Box>
                                <Box sx={{ mt: 1, ml: -1 }}>
                                    <RowActions onEdit={openEdit} subscription={s} onMarkPaid={openMarkPaid} onView={setViewing} onCancel={openCancel} />
                                </Box>
                            </Box>
                        ))}
                    </Box>
                    {pagination}
                </Frame>
            );
        }

        return (
            <Frame>
                <TableContainer sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity 120ms' }} aria-busy={loading}>
                    <Table size="small" sx={{ minWidth: 940 }}>
                        <TableHead>
                            <TableRow sx={headRowSx}>
                                <TableCell sx={{ pl: 2 }}>Customer</TableCell>
                                <TableCell>Account</TableCell>
                                <TableCell>Plan</TableCell>
                                <TableCell align="right">Amount</TableCell>
                                <TableCell>Period</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right" sx={{ pr: 2 }}>
                                    <Box component="span" sx={visuallyHidden}>Actions</Box>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {visible.map((s) => (
                                <TableRow key={s._id} hover sx={{ '& td': { borderColor: RULE, py: 1.25 } }}>
                                    <TableCell sx={{ pl: 2, maxWidth: 220 }}><Customer user={s.user} /></TableCell>
                                    <TableCell sx={{ maxWidth: 200 }}><Account account={s.whatsappAccount} /></TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ color: INK }}>{capitalize(s.plan)}</Typography>
                                        <Typography variant="body2" sx={{ color: MUTED }}>{CYCLE_LABELS[s.billingCycle] || 'Monthly'}</Typography>
                                    </TableCell>
                                    <TableCell align="right" sx={{ color: INK, fontWeight: 600, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                                        {formatMoney(s.amount, s.currency)}
                                    </TableCell>
                                    <TableCell><Period subscription={s} /></TableCell>
                                    <TableCell><StatusDot status={s.status} /></TableCell>
                                    <TableCell align="right" sx={{ pr: 1 }}>
                                        <RowActions onEdit={openEdit} subscription={s} onMarkPaid={openMarkPaid} onView={setViewing} onCancel={openCancel} />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                {pagination}
            </Frame>
        );
    };

    const truncated = serverTotal > subscriptions.length;
    const subline = () => {
        if (loading && !subscriptions.length) return 'Loading subscriptions…';
        if (loadError && !subscriptions.length) return 'Subscriptions unavailable';
        const count = hasFilters ? filtered.length : serverTotal;
        const text = `${formatCount(count)} ${count === 1 ? 'subscription' : 'subscriptions'}`;
        if (hasFilters) return `${text} match your filters`;
        return truncated ? `${text} · showing the latest ${formatCount(subscriptions.length)}` : text;
    };

    const showFigures = subscriptions.length > 0;

    return (
        <AppLayout>
            <Container maxWidth="lg" sx={{ pb: 6 }}>
                {/* Header */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-end', gap: 2, mb: 3 }}>
                    <Box>
                        <Typography
                            component="h1"
                            sx={{ fontSize: { xs: '1.75rem', md: '2.25rem' }, fontWeight: 700, letterSpacing: '-0.025em', color: INK, lineHeight: 1.15 }}
                        >
                            Subscriptions
                        </Typography>
                        <Typography variant="body2" sx={{ color: MUTED, mt: 0.75 }}>{subline()}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        <Button
                            variant="outlined"
                            startIcon={<FileDownloadOutlinedIcon />}
                            onClick={exportCsv}
                            disabled={!filtered.length}
                            sx={buttonSx}
                        >
                            Export for Tally
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<AutorenewIcon />}
                            onClick={() => setOpenRenewDialog(true)}
                            disabled={loading && !subscriptions.length}
                            sx={buttonSx}
                        >
                            Run renewals
                        </Button>
                        <Button variant="contained" startIcon={<AddIcon />} onClick={openAssign} sx={{ ...buttonSx, fontWeight: 600 }}>
                            Assign subscription
                        </Button>
                    </Box>
                </Box>

                {showFigures && (
                    <FigureStrip
                        figures={[
                            { label: 'Active', value: formatCount(figures.active) },
                            {
                                label: 'Awaiting payment',
                                value: formatCount(figures.pending),
                                hint: figures.pending ? 'Mark as paid once the money arrives' : 'Nothing to collect'
                            },
                            {
                                label: `Renewing in next ${RENEWAL_WINDOW_DAYS} days`,
                                value: formatCount(figures.renewing),
                                hint: dueNow ? `${formatCount(dueNow)} due now` : 'Includes any that are overdue'
                            },
                            {
                                label: 'Monthly revenue',
                                value: formatINR(Math.round(figures.monthlyRevenue)),
                                hint: 'Active plans, quarterly and annual spread per month'
                            }
                        ]}
                    />
                )}
                {loading && !subscriptions.length && (
                    <Skeleton variant="rounded" height={104} sx={{ mb: 3 }} />
                )}

                {/* Toolbar */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: { xs: 1.5, md: 2 }, mb: 2 }}>
                    <TextField
                        size="small"
                        placeholder="Search customer, account or phone"
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                        inputProps={{ 'aria-label': 'Search subscriptions' }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" sx={{ color: MUTED }} />
                                </InputAdornment>
                            ),
                            endAdornment: search ? (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setSearch('')} aria-label="Clear search">
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ) : null
                        }}
                        sx={{ flex: { xs: '1 1 100%', md: '1 1 300px' }, maxWidth: { md: 380 }, bgcolor: 'background.paper' }}
                    />

                    <Box sx={{ maxWidth: '100%', overflowX: 'auto' }}>
                        <ToggleButtonGroup
                            size="small"
                            exclusive
                            value={statusFilter}
                            onChange={(e, value) => { if (value !== null) { setStatusFilter(value); setPage(0); } }}
                            aria-label="Filter by status"
                        >
                            {STATUS_FILTERS.map((option) => (
                                <ToggleButton key={option.label} value={option.value} sx={{ textTransform: 'none', px: 1.5, whiteSpace: 'nowrap' }}>
                                    {option.label}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    </Box>

                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel id="cycle-filter-label">Billing cycle</InputLabel>
                        <Select
                            labelId="cycle-filter-label"
                            label="Billing cycle"
                            value={cycleFilter}
                            onChange={(e) => { setCycleFilter(e.target.value); setPage(0); }}
                        >
                            <MenuItem value="">Any cycle</MenuItem>
                            {Object.entries(CYCLE_LABELS).map(([value, label]) => (
                                <MenuItem key={value} value={value}>{label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {hasFilters && (
                        <Button size="small" onClick={clearFilters} sx={{ ...buttonSx, color: MUTED }}>
                            Clear filters
                        </Button>
                    )}
                </Box>

                {renderList()}

                {/* Mark as paid */}
                <Dialog
                    open={!!paying}
                    onClose={() => !processing && setPaying(null)}
                    maxWidth="sm"
                    fullWidth
                    fullScreen={fullScreenDialog}
                >
                    <Box component="form" onSubmit={handleProcessPayment}>
                        <DialogTitle sx={{ color: INK, fontWeight: 600 }}>Mark as paid</DialogTitle>
                        <DialogContent>
                            <Typography variant="body2" sx={{ color: MUTED, mb: 2 }}>
                                This activates the subscription, creates a paid invoice and notifies the customer.
                            </Typography>
                            {paying && <SubscriptionSummary subscription={paying} />}
                            <TextField
                                name="transactionId"
                                label="Transaction ID"
                                fullWidth
                                size="small"
                                value={processData.transactionId}
                                onChange={handleProcessChange}
                                margin="dense"
                                helperText="Optional. The bank or UPI reference, for your records."
                            />
                            <TextField
                                name="notes"
                                label="Admin notes"
                                fullWidth
                                size="small"
                                multiline
                                minRows={3}
                                value={processData.notes}
                                onChange={handleProcessChange}
                                margin="dense"
                                helperText="Optional. Only admins can see these."
                            />
                        </DialogContent>
                        <DialogActions sx={{ px: 3, pb: 2 }}>
                            <Button onClick={() => setPaying(null)} disabled={processing} sx={buttonSx}>Cancel</Button>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={processing}
                                startIcon={processing ? <CircularProgress size={16} color="inherit" /> : null}
                                sx={{ ...buttonSx, fontWeight: 600 }}
                            >
                                {processing ? 'Saving…' : 'Mark as paid'}
                            </Button>
                        </DialogActions>
                    </Box>
                </Dialog>

                {/* Details */}
                <Dialog open={!!viewing} onClose={() => setViewing(null)} maxWidth="sm" fullWidth fullScreen={fullScreenDialog}>
                    <DialogTitle sx={{ color: INK, fontWeight: 600 }}>Subscription details</DialogTitle>
                    <DialogContent>
                        {viewing && (
                            <>
                                <SubscriptionSummary subscription={viewing} />
                                <DetailRow label="Status"><StatusDot status={viewing.status} /></DetailRow>
                                <DetailRow label="Period">{formatDate(viewing.startDate)} – {formatDate(viewing.endDate)}</DetailRow>
                                <DetailRow label="Auto-renew">{viewing.autoRenew === false ? 'Off' : 'On'}</DetailRow>
                                <DetailRow label="Next renewal">{formatDate(viewing.nextRenewalDate || viewing.endDate)}</DetailRow>
                                <DetailRow label="Last renewed">{viewing.lastRenewalDate ? formatDate(viewing.lastRenewalDate) : 'Never'}</DetailRow>
                                <DetailRow label="Payment method">{PAYMENT_METHOD_LABELS[viewing.paymentMethod] || capitalize(viewing.paymentMethod) || '–'}</DetailRow>
                                <DetailRow label="Transaction ID">{viewing.transactionId || '–'}</DetailRow>
                                <DetailRow label="Marked as paid">{viewing.processedAt ? formatDateTime(viewing.processedAt) : 'Not yet'}</DetailRow>
                                <DetailRow label="Admin notes">{viewing.adminNotes || '–'}</DetailRow>
                                <DetailRow label="Created">{formatDateTime(viewing.createdAt)}</DetailRow>
                            </>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        {viewing?.status === 'pending' && (
                            <Button onClick={() => openMarkPaid(viewing)} sx={buttonSx}>Mark as paid</Button>
                        )}
                        <Button onClick={() => { const v = viewing; setViewing(null); openEdit(v); }} sx={buttonSx}>Edit subscription</Button>
                        <Button onClick={() => setViewing(null)} variant="outlined" sx={buttonSx}>Close</Button>
                    </DialogActions>
                </Dialog>

                <SubscriptionForm
                    open={!!formState.open}
                    subscription={formState.subscription}
                    initialUserId={formState.userId}
                    onClose={() => setFormState({ open: false })}
                    onSaved={handleSaved}
                />

                {/* Run renewals */}
                <Dialog open={openRenewDialog} onClose={() => !renewLoading && setOpenRenewDialog(false)} maxWidth="xs" fullWidth>
                    <DialogTitle sx={{ color: INK, fontWeight: 600 }}>Run renewals now?</DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" sx={{ color: INK, mb: 1.5 }}>
                            {dueNow
                                ? `${formatCount(dueNow)} active ${dueNow === 1 ? 'subscription has' : 'subscriptions have'} reached the end date with auto-renew on.`
                                : 'No loaded subscriptions have reached their end date yet. You can still run renewals to check.'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: MUTED }}>
                            Auto-renewing plans move on to their next period, ending on a month boundary; plans with auto-renew off
                            are marked expired and the customer is notified. This also runs automatically every night.
                            Plan fees are billed on the monthly invoices (Admin, Invoices).
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button onClick={() => setOpenRenewDialog(false)} disabled={renewLoading} sx={buttonSx}>Cancel</Button>
                        <Button
                            onClick={handleProcessMonthlyRenewals}
                            variant="contained"
                            disabled={renewLoading}
                            startIcon={renewLoading ? <CircularProgress size={16} color="inherit" /> : null}
                            sx={{ ...buttonSx, fontWeight: 600 }}
                        >
                            {renewLoading ? 'Running…' : 'Run renewals'}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={!!cancelTarget} onClose={() => !cancelling && setCancelTarget(null)} maxWidth="xs" fullWidth>
                    <DialogTitle sx={{ color: INK, fontWeight: 600 }}>Cancel this subscription?</DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" sx={{ color: MUTED, mb: 2 }}>
                            The subscription stops renewing. Plan fees are charged up to the day cover ends, so a part month is
                            charged for the days used. The customer is notified.
                        </Typography>
                        {cancelTarget && <SubscriptionSummary subscription={cancelTarget} />}
                        <FormControl sx={{ mt: 2 }}>
                            <FormLabel id="cancel-when" sx={{ color: INK, fontWeight: 600, fontSize: '0.875rem', '&.Mui-focused': { color: INK } }}>
                                Cover ends
                            </FormLabel>
                            <RadioGroup aria-labelledby="cancel-when" value={cancelWhen} onChange={(e) => setCancelWhen(e.target.value)}>
                                <FormControlLabel
                                    value="month_end"
                                    control={<Radio size="small" />}
                                    label={`At the end of this month, ${formatDate(endOfThisMonth())}`}
                                />
                                <FormControlLabel
                                    value="immediate"
                                    control={<Radio size="small" />}
                                    label={`Immediately, today ${formatDate(new Date())}`}
                                />
                            </RadioGroup>
                            {cancelTarget?.endDate && new Date(cancelTarget.endDate) < endOfThisMonth() && cancelWhen === 'month_end' && (
                                <Typography variant="caption" sx={{ color: MUTED, mt: 0.5 }}>
                                    This subscription already ends {formatDate(cancelTarget.endDate)}, so cover is not extended.
                                </Typography>
                            )}
                        </FormControl>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button onClick={() => setCancelTarget(null)} disabled={cancelling} sx={buttonSx}>Keep subscription</Button>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={handleCancel}
                            disabled={cancelling}
                            startIcon={cancelling ? <CircularProgress size={16} color="inherit" /> : null}
                            sx={{ ...buttonSx, fontWeight: 600 }}
                        >
                            {cancelling ? 'Cancelling…' : 'Cancel subscription'}
                        </Button>
                    </DialogActions>
                </Dialog>

                                <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                    <Alert onClose={() => setError(null)} severity="error" variant="filled" sx={{ width: '100%' }}>{error}</Alert>
                </Snackbar>
                <Snackbar open={!!success} autoHideDuration={4000} onClose={() => setSuccess(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                    <Alert onClose={() => setSuccess(null)} severity="success" variant="filled" sx={{ width: '100%' }}>{success}</Alert>
                </Snackbar>
            </Container>
        </AppLayout>
    );
};

const visuallyHidden = {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    whiteSpace: 'nowrap',
    width: 1
};

export default SubscriptionManagement;
